"use client";

import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/auth/use-auth";
import { AgentKnowledgeBaseSettings } from "@/components/agents/shared/knowledge-base-settings";
import type { AgentRagSettings } from "@/lib/agents/shared/types";
import type { MessageAgentConfig } from "@/lib/agents/messaging/types";

interface KnowledgeBaseSettingsProps {
  agentId: string;
  canManage: boolean;
}

export function KnowledgeBaseSettings({ agentId, canManage }: KnowledgeBaseSettingsProps) {
  const { fetchWithAuth, isAuthenticated } = useAuth();
  const [config, setConfig] = useState<MessageAgentConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await fetchWithAuth(`/api/agents/messaging/${agentId}`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data.agent?.config || {});
      }
    } catch (err) {
      console.error("Error loading agent config:", err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, agentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveKnowledgeBases = useCallback(
    async (kbIds: string[]): Promise<boolean> => {
      try {
        const response = await fetchWithAuth(`/api/agents/messaging/${agentId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            config: { ...config, knowledge_bases: kbIds },
          }),
        });

        if (response.ok) {
          setConfig((prev) => prev ? { ...prev, knowledge_bases: kbIds } : prev);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [fetchWithAuth, agentId, config]
  );

  const saveRagSettings = useCallback(
    async (settings: AgentRagSettings): Promise<boolean> => {
      try {
        const response = await fetchWithAuth(`/api/agents/messaging/${agentId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            config: { ...config, rag_settings: settings },
          }),
        });

        if (response.ok) {
          setConfig((prev) => prev ? { ...prev, rag_settings: settings } : prev);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [fetchWithAuth, agentId, config]
  );

  if (isLoading || !config) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <AgentKnowledgeBaseSettings
      agentType="message"
      agentId={agentId}
      selectedKnowledgeBases={config.knowledge_bases || []}
      ragSettings={config.rag_settings}
      onSaveKnowledgeBases={saveKnowledgeBases}
      onSaveAgentRagSettings={saveRagSettings}
      canManage={canManage}
    />
  );
}
